/**
 * Softphone – suivi de l'appel (docs/TELEPHONIE.md), sous l'appel en cours puis pour le dernier appel terminé (jusqu'au
 * suivant ou « Masquer ») :
 *  - petit bouton « REC » : rouge = appel enregistré, transcrit et résumé (par défaut) ; gris = enregistrement suspendu,
 *    et rien n'est gardé de l'appel (ni enregistrement, ni transcription, ni résumé). Modifiable pendant l'appel ;
 *  - menu « Affecter » (droit « modifier » sur les appels de la filiale du softphone, contrôlé par l'API : l'en-tête peut
 *    afficher une autre filiale ou la vue consolidée) : correspondant reconnu (contact, tiers), objets ouverts qui
 *    le concernent ou que l'utilisateur vient de créer (coche = affecté ; un clic affecte ou retire), puis « Nouvel… » :
 *    objets à créer depuis l'appel (FLUX_APPEL du registre, sortes du poste de l'utilisateur d'abord) – formulaire du
 *    module sous le menu, l'appel est affecté à l'objet créé.
 * API : PUT /v1/service/appels/softphone/{contact}/enregistrement|affectations (fournisseur Softphone), GET
 * /v1/service/appels/propositions.
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import type { ContactAppel, PropositionsAffectation, RessourceLieeAppel, TiersAppel } from '@agora/shared';
import { Button, Icon, Menu, Skeleton, cx, type MenuItem } from '../../../design';
import { FLUX_APPEL, type FluxAppel } from '../../../metiers/registre';
import { useFormats, useT } from '../../../socle';
import { useSocle } from '../../contexte';
import { SORTES_OBJETS_APPEL } from '../sortes';
import type { ContexteSoftphone } from './Softphone';

/** Appel suivi par le panneau : l'appel en cours, puis le dernier appel jusqu'au suivant. */
export interface SuiviAppel {
  contactId: string;
  numero: string | null;
  /** Début de l'appel (ISO 8601). */
  debut: string;
  /** Correspondant reconnu (titre affiché, contact, tiers), repris de l'identification pendant l'appel. */
  titre: string;
  tiers: TiersAppel | null;
  contact: ContactAppel | null;
  /** Bouton d'enregistrement : rouge (vrai) par défaut. */
  enregistrement: boolean;
  affectations: { ressource: RessourceLieeAppel; objetId: string; libelle?: string }[];
  termine: boolean;
  erreur?: 'enregistrement' | 'affectation';
}

const SORTES = SORTES_OBJETS_APPEL;

const cleObjet = (ressource: string, objetId: string) => `${ressource}|${objetId}`;

export function SuiviDeLAppel({ s, suivi }: { s: ContexteSoftphone; suivi: SuiviAppel }) {
  const { t } = useT();
  const fmt = useFormats();
  const [flux, setFlux] = React.useState<FluxAppel | null>(null);
  // Un autre appel : le formulaire de création de l'appel précédent est abandonné.
  React.useEffect(() => setFlux(null), [suivi.contactId]);
  const correspondant = suivi.numero && suivi.titre && suivi.titre !== suivi.numero ? `${suivi.titre} (${suivi.numero})` : suivi.titre || suivi.numero || '—';

  return (
    <section className="agx-softphone-suivi" aria-label={t('service.telephonie.affectation.titre')}>
      {suivi.termine ? (
        <div className="agx-softphone-options">
          <p className="agx-softphone-aide">{t('service.telephonie.affectation.dernierAppel', { correspondant })}</p>
          <Button variant="quiet" size="sm" icon="close" aria-label={t('service.telephonie.affectation.oublier')} onClick={s.oublierAppel} />
        </div>
      ) : null}
      <div className="agx-softphone-options">
        <Enregistrement suivi={suivi} basculer={s.basculerEnregistrement} />
        <MenuAffecter s={s} suivi={suivi} creer={setFlux} />
      </div>
      {suivi.erreur ? <p className="agx-softphone-erreur" role="alert">{t(`service.telephonie.affectation.erreurs.${suivi.erreur}`)}</p> : null}
      {flux ? (
        <section className="agx-softphone-flux" aria-label={t(flux.libelle)}>
          <h3>{t(flux.libelle)}</h3>
          <React.Suspense fallback={<Skeleton variant="block" size="sm" />}>
            <flux.composant
              filiale={s.filiale} numero={suivi.numero} tiers={suivi.tiers} contact={suivi.contact}
              origine={t('service.telephonie.affectation.origine', { date: fmt.dateHeure(suivi.debut), correspondant })}
              fermer={(cree) => {
                setFlux(null);
                if (cree) void s.affecter(flux.ressource, cree.id, cree.libelle, true);
              }}
            />
          </React.Suspense>
        </section>
      ) : null}
    </section>
  );
}

/**
 * Bouton « REC » : pendant l'appel, bascule rouge / gris (aria-pressed) ; une fois l'appel terminé, simple indication
 * (l'enregistrement est déjà gardé ou supprimé).
 */
function Enregistrement({ suivi, basculer }: { suivi: SuiviAppel; basculer: () => void }) {
  const { t } = useT();
  const actif = suivi.enregistrement;
  const classe = cx('agx-softphone-rec', actif && 'agx-softphone-rec-actif');
  if (suivi.termine) {
    return (
      <span className={classe}>
        <span className="agx-softphone-rec-point" aria-hidden="true" />
        {t(actif ? 'service.telephonie.affectation.enregistre' : 'service.telephonie.affectation.nonEnregistre')}
      </span>
    );
  }
  return (
    <Button
      variant="quiet" size="sm" className={classe} aria-pressed={actif} onClick={basculer}
      aria-label={`${t('service.telephonie.affectation.rec')} · ${t('service.telephonie.affectation.enregistrement')}`}
      title={t(actif ? 'service.telephonie.affectation.enregistrementActif' : 'service.telephonie.affectation.enregistrementInactif')}
    >
      <span className="agx-softphone-rec-point" aria-hidden="true" />
      {t('service.telephonie.affectation.rec')}
    </Button>
  );
}

function MenuAffecter({ s, suivi, creer }: { s: ContexteSoftphone; suivi: SuiviAppel; creer: (f: FluxAppel) => void }) {
  const { t } = useT();
  const { api } = useSocle();
  const { filiale } = s;
  const [ouvert, setOuvert] = React.useState(false);
  const contact = suivi.contact?.id;
  const tiers = suivi.tiers?.id;
  // Relu à chaque ouverture : un objet créé ailleurs entre-temps apparaît.
  const { data } = useQuery({
    queryKey: ['softphone', 'propositions', filiale, contact, tiers],
    enabled: ouvert,
    staleTime: 0,
    queryFn: ({ signal }) => api.get<PropositionsAffectation>('/v1/service/appels/propositions', {
      filiale, ...(contact ? { contact } : {}), ...(tiers ? { tiers } : {}),
    }, signal),
  });

  const affectes = new Map(suivi.affectations.map((a) => [cleObjet(a.ressource, a.objetId), a]));
  const libelles = new Map<string, { ressource: RessourceLieeAppel; objetId: string; libelle: string }>();
  for (const o of data?.objets ?? []) {
    libelles.set(cleObjet(o.ressource, o.objetId), { ...o, libelle: o.code ? `${o.code} · ${o.libelle}` : o.libelle });
  }
  for (const a of suivi.affectations) {
    if (!libelles.has(cleObjet(a.ressource, a.objetId))) {
      libelles.set(cleObjet(a.ressource, a.objetId), { ...a, libelle: a.libelle ?? t(`service.telephonie.affectation.ressources.${SORTES[a.ressource].cle}`) });
    }
  }
  const items: MenuItem[] = [...libelles.entries()].map(([id, o]) => ({
    id: `objet:${id}`,
    label: o.libelle,
    leading: affectes.has(id)
      ? <Icon name="check" label={t('service.telephonie.affectation.affecte')} />
      : <Icon name={SORTES[o.ressource].icone} />,
    trailing: <small>{t(`service.telephonie.affectation.ressources.${SORTES[o.ressource].cle}`)}</small>,
  }));
  if (!data) items.push({ id: 'chargement', label: t('service.telephonie.affectation.chargement'), disabled: true });
  else if (!items.length) items.push({ id: 'aucun', label: t('service.telephonie.affectation.aucun'), disabled: true });
  const flux = (data?.creations ?? [])
    .map((r) => FLUX_APPEL.find((f) => f.ressource === r))
    .filter((f): f is FluxAppel => !!f && (!f.tiersRequis || !!suivi.tiers));
  if (flux.length) {
    items.push({ divider: true });
    for (const f of flux) items.push({ id: `creer:${f.ressource}`, label: t(f.libelle), icon: 'plus' });
  }

  const choisir = (id: string) => {
    if (id.startsWith('creer:')) {
      const f = flux.find((x) => `creer:${x.ressource}` === id);
      if (f) creer(f);
      return;
    }
    const o = libelles.get(id.slice('objet:'.length));
    if (o) void s.affecter(o.ressource, o.objetId, o.libelle, !affectes.has(cleObjet(o.ressource, o.objetId)));
  };

  return (
    <Menu
      items={items} onSelect={choisir} open={ouvert} onOpenChange={setOuvert} align="end"
      ariaLabel={t('service.telephonie.affectation.affecterAide')}
      trigger={(
        <Button size="sm" icon="lien">
          {t('service.telephonie.affectation.affecter')}{suivi.affectations.length ? ` (${suivi.affectations.length})` : ''}
        </Button>
      )}
    />
  );
}
